import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class SurveyStorageService {
  private readonly DISMISSED_SURVEYS_KEY = 'dismissed_surveys';
  private readonly DEFAULT_DISMISS_HOURS = 24;

  constructor() {
    // Make service available globally for debugging
    if (typeof window !== 'undefined') {
      (window as any).surveyStorageService = this;
    }
  }

  /**
   * Mark a survey as dismissed for a specific user
   * Survey will be available again the next day after 10am
   */
  async dismissSurvey(surveyId: string, userId: string, dismissHours: number = this.DEFAULT_DISMISS_HOURS): Promise<void> {
    try {
      const dismissedSurveys = await this.getDismissedSurveys();
      
      // Calculate dismiss until time - next day after 10am
      const now = new Date();
      const dismissUntil = new Date(now);
      
      // Move to next day
      dismissUntil.setDate(dismissUntil.getDate() + 1);
      
      // Set time to 10:00:00 AM
      dismissUntil.setHours(10, 0, 0, 0);

      const key = `${userId}_${surveyId}`;
      dismissedSurveys[key] = {
        surveyId,
        userId,
        dismissedAt: new Date().toISOString(),
        dismissUntil: dismissUntil.toISOString()
      };

      await Preferences.set({
        key: this.DISMISSED_SURVEYS_KEY,
        value: JSON.stringify(dismissedSurveys)
      });

      // // console.log(`Survey ${surveyId} dismissed for user ${userId} until ${dismissUntil.toISOString()}`);
    } catch (error) {
      // // console.error('Error dismissing survey:', error);
    }
  }

  /**
   * Check if a survey is currently dismissed for a user
   */
  async isSurveyDismissed(surveyId: string, userId: string): Promise<boolean> {
    try {
      const dismissedSurveys = await this.getDismissedSurveys();
      const key = `${userId}_${surveyId}`;
      const dismissRecord = dismissedSurveys[key];

      console.log(`Checking dismissal for survey ${surveyId}, user ${userId}`);
      console.log(`All dismissed surveys:`, Object.keys(dismissedSurveys));
      
      if (!dismissRecord) {
        console.log(`No dismiss record found for survey ${surveyId} and user ${userId}`);
        return false;
      }

      const now = new Date();
      const dismissUntil = new Date(dismissRecord.dismissUntil);

      console.log(`Found dismiss record:`, {
        dismissedAt: dismissRecord.dismissedAt,
        dismissUntil: dismissRecord.dismissUntil,
        now: now.toISOString(),
        isStillDismissed: now < dismissUntil
      });

      if (now < dismissUntil) {
        console.log(`Survey ${surveyId} is dismissed for user ${userId} until ${dismissUntil.toISOString()}`);
        return true;
      } else {
        // Dismiss period has expired, remove the record
        console.log(`Survey ${surveyId} dismiss period expired, removing record`);
        delete dismissedSurveys[key];
        await Preferences.set({
          key: this.DISMISSED_SURVEYS_KEY,
          value: JSON.stringify(dismissedSurveys)
        });
        return false;
      }
    } catch (error) {
      // console.error('Error checking dismissed survey:', error);
      return false;
    }
  }

  /**
   * Get all dismissed surveys (for debugging/cleanup)
   */
  private async getDismissedSurveys(): Promise<any> {
    try {
      const result = await Preferences.get({ key: this.DISMISSED_SURVEYS_KEY });
      return result.value ? JSON.parse(result.value) : {};
    } catch (error) {
      // console.error('Error getting dismissed surveys:', error);
      return {};
    }
  }

  /**
   * Clear all dismissed surveys (for debugging/reset)
   */
  async clearAllDismissedSurveys(): Promise<void> {
    try {
      await Preferences.remove({ key: this.DISMISSED_SURVEYS_KEY });
      console.log('All dismissed surveys cleared');
    } catch (error) {
      console.error('Error clearing dismissed surveys:', error);
    }
  }
  
  /**
   * Get all dismissed surveys for debugging
   */
  async getAllDismissedSurveys(): Promise<any> {
    return await this.getDismissedSurveys();
  }

  /**
   * Clear expired dismissed surveys
   */
  async cleanupExpiredDismissals(): Promise<void> {
    try {
      const dismissedSurveys = await this.getDismissedSurveys();
      const now = new Date();
      let hasChanges = false;

      for (const key in dismissedSurveys) {
        const dismissRecord = dismissedSurveys[key];
        const dismissUntil = new Date(dismissRecord.dismissUntil);
        
        if (now >= dismissUntil) {
          delete dismissedSurveys[key];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        await Preferences.set({
          key: this.DISMISSED_SURVEYS_KEY,
          value: JSON.stringify(dismissedSurveys)
        });
        // console.log('Expired survey dismissals cleaned up');
      }
    } catch (error) {
      // console.error('Error cleaning up dismissed surveys:', error);
    }
  }
}